import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, supportApi } from '../services/api';
import { CLIENT_KEY_STORAGE, SCREENS } from '../lib/screenRegistry';
import { ROUTES } from '../lib/classroomRoutes';
import { isMaintenancePreviewEmbed } from '../lib/masterAdminAccess';
import {
  GENDER_OPTIONS,
  formatVoiceProfileLabel,
  resolveVoiceFromMatrix,
  previewVoiceSample as playVoicePreviewSample,
} from '../lib/voiceProfile';
import './OrgAdmin.css';

const ORG_LOGO = encodeURI('/Assets/logo/Mentology Logo.png');
const ORG_LANGUAGE_OPTIONS = ['English (Aust)', 'English (Global)'] as const;
const SIDEBAR_SECTIONS = ['Subscription', 'Enrollment', 'Support'] as const;
type OrgAdminSection = (typeof SIDEBAR_SECTIONS)[number];

const NAV_LABELS: Record<OrgAdminSection, string> = {
  Subscription: 'Subscription',
  Enrollment: 'Enrolment',
  Support: 'Support',
};

interface OrgDashboard {
  org?: {
    id?: number;
    company_name: string;
    seat_count: number;
    org_token?: string;
  };
  activeSeats?: number;
}

export default function OrgAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<OrgAdminSection>('Subscription');
  const [dashboard, setDashboard] = useState<OrgDashboard | null>(null);
  const [clientKey, setClientKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [enrollForm, setEnrollForm] = useState({
    studentMentorName: '',
    preferedName: '',
    email: '',
    phone: '',
    languageTarget: 'English (Aust)',
    aiMentorGender: '',
  });
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
  });
  const [supportFiles, setSupportFiles] = useState<File[]>([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState('');
  const [supportSuccess, setSupportSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const loadDashboard = useCallback(() => {
    return authApi.orgDashboard().then((data) => {
      setDashboard(data);
    });
  }, []);

  useEffect(() => {
    const storedKey = sessionStorage.getItem(CLIENT_KEY_STORAGE);
    if (storedKey) {
      setClientKey(storedKey);
    }
  }, []);

  useEffect(() => {
    if (isMaintenancePreviewEmbed()) return;
    if (!user) {
      navigate(ROUTES.CLASSROOM_PORTAL, { replace: true });
      return;
    }
    if (user.role !== 'ORG') {
      if (user.accountState === 'INITIALIZING') {
        navigate(ROUTES.EXECUTIVE_INTRO, { replace: true });
      } else {
        navigate(ROUTES.LEARNING_ROOM, { replace: true });
      }
      return;
    }
    if (user.accountState === 'INITIALIZING') {
      navigate(ROUTES.EXECUTIVE_INTRO, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    loadDashboard().catch(() => {
      if (!import.meta.env.DEV && !isMaintenancePreviewEmbed()) {
        navigate(ROUTES.CLASSROOM_PORTAL);
      }
    });
  }, [loadDashboard, navigate]);

  const formatOrgId = (id?: number, orgToken?: string) =>
    orgToken || (id ? `MPL-N00-${String(id).padStart(4, '0')}` : 'MPL-N00-0001');

  const updateEnroll = (field: string, value: string) => {
    setEnrollForm((f) => ({ ...f, [field]: value }));
  };

  const handleEnrollmentVoicePreview = async () => {
    const entry = resolveVoiceFromMatrix(enrollForm.languageTarget, enrollForm.aiMentorGender);
    if (!entry) return;

    try {
      await playVoicePreviewSample(entry.voiceId, entry.name, {
        language: entry.language,
        gender: entry.gender,
        modelId: entry.modelId,
      });
    } catch (error) {
      console.error('[Voice preview]', error);
    }
  };

  const enrollStudentMentor = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setEnrollError('');
    try {
      await authApi.enrollMentee({
        menteeName: enrollForm.studentMentorName,
        menteeEmail: enrollForm.email,
        mentorName: '',
        mentorPhone: enrollForm.phone,
        mentorEmail: '',
      });
      await loadDashboard();
    } catch (err: unknown) {
      setEnrollError((err as { error?: string }).error || 'Student enrollment failed.');
    } finally {
      setLoading(false);
    }
  };

  const updateSupport = (field: string, value: string) => {
    setSupportForm((f) => ({ ...f, [field]: value }));
    setSupportSuccess('');
  };

  const addSupportFiles = (files: FileList | null) => {
    if (!files) return;
    setSupportFiles((prev) => [...prev, ...Array.from(files)]);
    setSupportSuccess('');
  };

  const readFileAsBase64 = (file: File) =>
    new Promise<{ filename: string; mimeType: string; size: number; data: string }>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
          data: result.split(',')[1] || '',
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const submitSupportTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    setSupportLoading(true);
    setSupportError('');
    setSupportSuccess('');
    try {
      const attachments = await Promise.all(supportFiles.map(readFileAsBase64));
      const result = await supportApi.submitTicket({
        ...supportForm,
        attachments,
      });
      setSupportSuccess(result.message || 'Support ticket successfully transmitted to Mentology Operations.');
      setSupportForm({ name: '', email: '', phone: '', description: '' });
      setSupportFiles([]);
    } catch (err: unknown) {
      setSupportError((err as { error?: string }).error || 'Support ticket submission failed.');
    } finally {
      setSupportLoading(false);
    }
  };

  const voiceEntry = resolveVoiceFromMatrix(enrollForm.languageTarget, enrollForm.aiMentorGender);
  const voiceProfile = voiceEntry ? formatVoiceProfileLabel(voiceEntry) : null;

  const renderPanel = () => {
    switch (activeSection) {
      case 'Subscription':
        return (
          <div className="admin-view-panel">
            <h3 className="admin-view-heading">Subscription Governance</h3>
            <div className="admin-governance-stack">
              <div className="admin-governance-row">
                <div>
                  <p className="admin-governance-row-title">Upgrade Plan</p>
                  <p className="admin-governance-row-desc">Increase available corporate seat limits</p>
                </div>
                <button type="button" className="admin-console-action-btn">Upgrade</button>
              </div>
              <div className="admin-governance-row">
                <div>
                  <p className="admin-governance-row-title">Workspace Capacity</p>
                  <p className="admin-governance-row-desc warning">3 seats left</p>
                </div>
                <div className="admin-governance-row-stat">Current: 5 Seats</div>
              </div>
              <div className="admin-governance-row">
                <div>
                  <p className="admin-governance-row-title danger">Purge Workspace</p>
                  <p className="admin-governance-row-desc">
                    Delete all group records and historical metrics (Irreversible)
                  </p>
                </div>
                <button type="button" className="admin-console-action-btn danger">Delete All Files</button>
              </div>
            </div>
          </div>
        );
      case 'Enrollment':
        return (
          <div className="enrollment-workspace-shell">
            <h3 className="admin-view-heading">Student Enrolment</h3>
            <div className="enrollment-workspace-scroll">
              <form id="org-enrollment-form" onSubmit={enrollStudentMentor} className="enrollment-form-stack">
                <div className="enrollment-mockup-grid">
                  <div>
                    <label className="enrollment-mockup-label" htmlFor="enroll-student-mentor-name">
                      Student Name
                    </label>
                    <input
                      id="enroll-student-mentor-name"
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.studentMentorName}
                      onChange={(e) => updateEnroll('studentMentorName', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="enrollment-mockup-label" htmlFor="enroll-prefered-name">
                      Prefered Name
                    </label>
                    <input
                      id="enroll-prefered-name"
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.preferedName}
                      onChange={(e) => updateEnroll('preferedName', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="enrollment-mockup-label" htmlFor="enroll-email">
                      Email
                    </label>
                    <input
                      id="enroll-email"
                      className="input-field enrollment-field-input"
                      type="email"
                      value={enrollForm.email}
                      onChange={(e) => updateEnroll('email', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="enrollment-mockup-label" htmlFor="enroll-phone">
                      Phone
                    </label>
                    <input
                      id="enroll-phone"
                      className="input-field enrollment-field-input"
                      type="tel"
                      inputMode="numeric"
                      value={enrollForm.phone}
                      onChange={(e) => updateEnroll('phone', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="enrollment-mockup-label" htmlFor="enroll-language">
                      Language Target
                    </label>
                    <select
                      id="enroll-language"
                      className="input-field enrollment-field-input enrollment-field-select"
                      value={enrollForm.languageTarget}
                      onChange={(e) => updateEnroll('languageTarget', e.target.value)}
                    >
                      {ORG_LANGUAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="enrollment-mockup-label" htmlFor="enroll-voice">
                      AI-Study Partner Voice
                    </label>
                    <select
                      id="enroll-voice"
                      className="input-field enrollment-field-input enrollment-field-select"
                      value={enrollForm.aiMentorGender}
                      onChange={(e) => updateEnroll('aiMentorGender', e.target.value)}
                    >
                      <option value="">Select voice profile</option>
                      {GENDER_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {voiceProfile && (
                    <div className="col-span-2 enrollment-voice-preview-bar">
                      <span className="enrollment-voice-profile-label">
                        Voice Profile: {voiceProfile}
                      </span>
                      <button
                        type="button"
                        className="enrollment-voice-preview-btn"
                        onClick={handleEnrollmentVoicePreview}
                      >
                        [ ▶ Preview Voice Sample ]
                      </button>
                    </div>
                  )}
                </div>

                {enrollError && <p className="error-text enrollment-form-error">{enrollError}</p>}
              </form>
            </div>
            <div className="enrollment-sticky-action">
              <button
                type="submit"
                form="org-enrollment-form"
                className="admin-console-primary-btn"
                disabled={loading}
              >
                {loading ? 'Activating...' : 'Activate Student License'}
              </button>
            </div>
          </div>
        );
      case 'Support':
        return (
          <div className="admin-view-panel">
            <h3 className="admin-view-heading">Institutional Support</h3>
            <p className="support-institutional-copy">
              For framework system anomalies or seat configuration overrides, contact backend data
              processing arrays at{' '}
              <a className="support-email-link" href="mailto:support@mentology.com.au">
                support@mentology.com.au
              </a>
              .
            </p>
            {supportSuccess && (
              <div className="support-success-banner" role="status">
                {supportSuccess}
              </div>
            )}
            <form onSubmit={submitSupportTicket} className="support-form">
              <label className="support-field-label">
                Name
                <input
                  className="input-field support-field-input"
                  type="text"
                  placeholder="The individual lodging the complaint"
                  value={supportForm.name}
                  onChange={(e) => updateSupport('name', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Email
                <input
                  className="input-field support-field-input"
                  type="email"
                  value={supportForm.email}
                  onChange={(e) => updateSupport('email', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Contact Phone
                <input
                  className="input-field support-field-input"
                  type="tel"
                  value={supportForm.phone}
                  onChange={(e) => updateSupport('phone', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Problem Description / Issue Details
                <textarea
                  className="input-field support-field-input support-textarea"
                  value={supportForm.description}
                  onChange={(e) => updateSupport('description', e.target.value)}
                  required
                  rows={3}
                />
              </label>
              <div
                className={`support-dropzone ${dragActive ? 'support-dropzone-active' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  addSupportFiles(e.dataTransfer.files);
                }}
              >
                <p className="support-dropzone-hint">Drag and drop files here, or use the picker below.</p>
                <label className="btn-primary support-file-label">
                  Attach Screenshots or Log Files
                  <input
                    type="file"
                    multiple
                    className="support-file-input"
                    onChange={(e) => addSupportFiles(e.target.files)}
                  />
                </label>
                {supportFiles.length > 0 && (
                  <ul className="support-file-list">
                    {supportFiles.map((file) => (
                      <li key={`${file.name}-${file.size}`}>{file.name} ({Math.round(file.size / 1024)} KB)</li>
                    ))}
                  </ul>
                )}
              </div>
              {supportError && <p className="error-text support-error">{supportError}</p>}
              <div className="support-action-row">
                <button type="submit" className="btn-primary support-action-btn" disabled={supportLoading}>
                  {supportLoading ? 'Transmitting...' : 'Submit Support Ticket'}
                </button>
              </div>
            </form>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-layout" data-screen-id={SCREENS.MPL08}>
      <aside className="admin-sidebar">
        <div className="admin-sidebar-top">
          <div className="admin-sidebar-brand">
            <img
              src={ORG_LOGO}
              alt="Mentology"
              className="admin-sidebar-logo brand-logo"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <h2 className="admin-sidebar-title">Classroom Admin Console</h2>
          </div>

          <div className="admin-sidebar-org-meta">
            <div>
              Organisation:{' '}
              <span>{dashboard?.org?.company_name || 'Mentology Dev Organisation'}</span>
            </div>
            <div>
              Org-ID:{' '}
              <span className="admin-org-id-mono">
                {formatOrgId(dashboard?.org?.id, dashboard?.org?.org_token)}
              </span>
            </div>
            <div>
              Subscription Status:{' '}
              <span className="admin-org-status-active">Active</span>
            </div>
          </div>

          <nav className="admin-sidebar-nav" aria-label="Administration sections">
            {SIDEBAR_SECTIONS.map((section) => (
              <button
                key={section}
                type="button"
                className={`admin-console-nav-btn ${activeSection === section ? 'active' : ''}`}
                onClick={() => setActiveSection(section)}
              >
                <span>{NAV_LABELS[section]}</span>
                <span aria-hidden="true">▶</span>
              </button>
            ))}
          </nav>
        </div>

        <p className="admin-sidebar-footer">Mentology Governance Framework © 2026</p>
      </aside>
      <main className="admin-panel">
        {clientKey && (
          <div className="org-client-key-banner">
            <span className="org-client-key-label">Active Client Key:</span>
            <span className="org-client-key-value">{clientKey}</span>
          </div>
        )}
        {!dashboard ? (
          <p className="scannable-bullet-text">Loading organisation console...</p>
        ) : (
          renderPanel()
        )}
      </main>
    </div>
  );
}
